import { useEffect, useState } from "react";
import { FileCheck2, Loader2, ReceiptText, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, downloadPdf, fmtDate, formatApiError, inr, todayISO, waLink } from "@/lib/api";
import { PAYMENT_MODES } from "@/lib/constants";
import { WhatsAppIcon } from "@/components/site/WhatsAppIcon";
import { payInfoText } from "@/lib/messages";
import { useAdminSettings } from "@/lib/useAdminSettings";

const receiptName = (b, p, i) => p.receipt_no || `${b.booking_no}-R${i + 1}`;
const downloadReceipt = (b, p, i) => downloadPdf(`/admin/bookings/${b.id}/payments/${p.id}/receipt.pdf`, `${receiptName(b, p, i)}.pdf`)
  .catch((e) => toast.error(formatApiError(e)));
const receiptText = (b, p, balance, s) =>
  `Hi ${b.customer_name}, we have received ${inr(p.amount)} on ${fmtDate(p.date)} via ${p.mode} for your Ocean Wings Travel booking ${b.booking_no}. ` +
  `Balance due: ${inr(balance)}.${balance > 0 ? payInfoText(s, b.booking_no) : ""}\n\nI'm sharing your payment receipt PDF here. Thank you!`;

const fresh = (b) => ({ date: todayISO(), amount: b?.balance_due || "", mode: "UPI", reference: "", note: "" });

const Totals = ({ b }) => (
  <div className="grid grid-cols-3 gap-3 rounded-xl bg-mist p-4 text-sm" data-testid="payments-summary">
    <div><p className="text-xs text-slate-500">Total price</p><p className="font-mono font-semibold">{inr(b.total_price)}</p></div>
    <div><p className="text-xs text-slate-500">Received</p><p className="font-mono font-semibold text-emerald-600" data-testid="payments-received">{inr(b.advance_paid)}</p></div>
    <div><p className="text-xs text-slate-500">Balance due</p><p className="font-mono font-semibold text-coral" data-testid="payments-balance">{inr(b.balance_due)}</p></div>
  </div>
);

export const PaymentsDialog = ({ booking, onClose, onChanged }) => {
  const settings = useAdminSettings();
  const [b, setB] = useState(booking);
  const [f, setF] = useState(fresh(booking));
  const [saving, setSaving] = useState(false);
  useEffect(() => { setB(booking); setF(fresh(booking)); }, [booking]);

  const update = (data) => { setB(data); setF(fresh(data)); onChanged(data); };
  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/bookings/${b.id}/payments`, { ...f, amount: Number(f.amount) });
      const idx = data.payments.findIndex((p) => !b.payments.some((x) => x.id === p.id));
      update(data);
      toast.success("Payment recorded", idx >= 0 ? { action: { label: "Download receipt", onClick: () => downloadReceipt(data, data.payments[idx], idx) } } : undefined);
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };
  const remove = async (pid) => {
    if (!window.confirm("Remove this payment?")) return;
    try { const { data } = await api.delete(`/admin/bookings/${b.id}/payments/${pid}`); update(data); toast.success("Payment removed"); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  return (
    <Dialog open={!!booking} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="payments-dialog">
        <DialogHeader>
          <DialogTitle>Payments · {b?.booking_no}</DialogTitle>
          <DialogDescription>{b?.customer_name} · {b?.package_name || "Custom package"}</DialogDescription>
        </DialogHeader>
        {b && (
          <div className="space-y-5">
            <Totals b={b} />
            <div className="rounded-xl border border-slate-200 divide-y divide-slate-100" data-testid="payments-list">
              {b.payments.length === 0 && <p className="p-4 text-sm text-slate-500" data-testid="payments-empty">No payments recorded yet.</p>}
              {b.payments.map((p, i) => {
                const balance = Math.max(b.total_price - b.payments.slice(0, i + 1).reduce((a, x) => a + x.amount, 0), 0);
                return (
                <div key={p.id} className="p-3 flex items-center gap-2" data-testid={`payment-row-${p.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-navy">{fmtDate(p.date)} · {p.mode}</p>
                    <p className="text-xs text-slate-500 truncate">{[receiptName(b, p, i), p.reference, p.note].filter(Boolean).join(" · ")}</p>
                  </div>
                  <p className="font-mono font-semibold text-emerald-600">{inr(p.amount)}</p>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-ocean" title="Download receipt" onClick={() => downloadReceipt(b, p, i)} data-testid={`payment-receipt-${p.id}`}><ReceiptText className="h-4 w-4" /></Button>
                  <a href={waLink(b.phone, receiptText(b, p, balance, settings))} target="_blank" rel="noreferrer" title="Send receipt on WhatsApp" data-testid={`payment-whatsapp-${p.id}`}
                    className="h-8 w-8 grid place-items-center rounded-lg text-emerald-600 hover:bg-emerald-50"><WhatsAppIcon className="h-4 w-4" /></a>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600" onClick={() => remove(p.id)} data-testid={`payment-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
                );
              })}
            </div>
            {b.balance_due > 0 ? (
              <form onSubmit={add} className="grid gap-3 sm:grid-cols-3 rounded-xl border border-sky-100 p-4" data-testid="payment-form">
                <p className="sm:col-span-3 font-semibold text-navy text-sm">Record a payment</p>
                <div className="space-y-1.5"><Label>Date *</Label><Input type="date" required value={f.date} onChange={set("date")} data-testid="payment-date-input" /></div>
                <div className="space-y-1.5"><Label>Amount (₹) *</Label><Input type="number" required min={1} max={b.balance_due} step="any" value={f.amount} onChange={set("amount")} data-testid="payment-amount-input" /></div>
                <div className="space-y-1.5"><Label>Mode *</Label>
                  <Select value={f.mode} onValueChange={(v) => setF((s) => ({ ...s, mode: v }))}>
                    <SelectTrigger data-testid="payment-mode-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{PAYMENT_MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Reference / Txn ID</Label><Input value={f.reference} onChange={set("reference")} maxLength={100} data-testid="payment-reference-input" /></div>
                <div className="space-y-1.5 sm:col-span-2"><Label>Note</Label><Input value={f.note} onChange={set("note")} maxLength={300} data-testid="payment-note-input" /></div>
                <Button type="submit" disabled={saving} className="sm:col-span-3 bg-ocean hover:bg-navy" data-testid="payment-add-button">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add payment"}
                </Button>
              </form>
            ) : <p className="text-sm font-semibold text-emerald-600" data-testid="payments-fully-paid">This booking is fully paid.</p>}
            <Button variant="outline" className="w-full" onClick={() => downloadPdf(`/admin/bookings/${b.id}/voucher.pdf`, `${b.booking_no}-confirmation.pdf`)} data-testid="payments-voucher-button">
              <FileCheck2 className="h-4 w-4 mr-2" />Download booking confirmation PDF
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
